import React, { useState } from 'react';
import { SectionType, PageSection } from '../../types';
import { useSite } from '../../context/SiteContext';
import { getAvailableSectionsForIndustry, COMPONENT_REGISTRY } from './registry';
import { X, Search, Plus, Sparkles, Layers } from 'lucide-react';
import { AISectionGeneratorModal } from '../ai/AISectionGeneratorModal';

interface AddSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  insertIndex?: number;
}

export const AddSectionModal: React.FC<AddSectionModalProps> = ({
  isOpen,
  onClose,
  insertIndex,
}) => {
  const { activeSite, addSectionToActivePage, addCustomSectionToActivePage } = useSite();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [isAiSectionModalOpen, setIsAiSectionModalOpen] = useState(false);

  if (!isOpen || !activeSite) return null;

  const availableSections = getAvailableSectionsForIndustry(activeSite.industry);

  const categories = [
    { id: 'ALL', name: 'Tümü' },
    { id: 'NAVIGATION', name: 'Navigasyon' },
    { id: 'HERO', name: 'Karşılama (Hero)' },
    { id: 'CONTENT', name: 'İçerik & Hizmetler' },
    { id: 'SOCIAL_PROOF', name: 'Sosyal Kanıt & Ekip' },
    { id: 'YACHT', name: 'Yat & Kiralama' },
    { id: 'MEDIA', name: 'Medya & Blog' },
    { id: 'CONTACT', name: 'İletişim' },
  ].filter((cat) => {
    if (cat.id === 'ALL') return true;
    return availableSections.some((s) => s.category === cat.id);
  });

  const filteredSections = availableSections.filter((sec) => {
    const matchesCategory =
      selectedCategory === 'ALL' || sec.category === selectedCategory;
    const matchesSearch =
      sec.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sec.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in select-none">
        <div className="bg-slate-900 border border-slate-800 w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh]">
          {/* Header */}
          <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/40">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-600/20 text-blue-400 flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Sayfaya Yeni Bölüm Ekle</h3>
                <p className="text-xs text-slate-400">
                  {activeSite.industry === 'YACHT_RENTAL' ? 'Yat Kiralama' : 'Kurumsal'} sektörü için optimize edilmiş bileşenler.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsAiSectionModalOpen(true)}
                className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white text-xs font-bold shadow-md shadow-indigo-600/20 transition flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>AI ile Bölüm Üret</span>
              </button>

              <button
                onClick={onClose}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

        {/* Filter & Search Bar */}
        <div className="p-4 border-b border-slate-800/80 bg-slate-950/20 flex flex-col sm:flex-row items-center gap-3">
          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Bölüm ara..."
              className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-800 border border-slate-700 text-xs text-white placeholder-slate-400 outline-none focus:border-blue-500"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full pb-1 sm:pb-0">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                  selectedCategory === cat.id
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Section Grid */}
        <div className="p-6 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSections.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.type}
                type="button"
                onClick={() => {
                  addSectionToActivePage(item.type, insertIndex);
                  onClose();
                }}
                className="text-left p-5 rounded-2xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/80 hover:border-blue-500 transition-all flex flex-col justify-between space-y-4 group shadow-sm hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <div className="space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700 flex items-center justify-center text-blue-400 group-hover:scale-110 group-hover:border-blue-500/40 transition">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-blue-300 transition">
                      {item.name}
                    </h4>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-700/60 flex items-center justify-between text-[10px] font-bold text-slate-400">
                  <span className="uppercase tracking-wider px-2 py-0.5 rounded bg-slate-900 border border-slate-700">
                    {item.category}
                  </span>
                  <span className="text-blue-400 group-hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" />
                    Ekle
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>

    {isAiSectionModalOpen && (
      <AISectionGeneratorModal
        industry={activeSite.industry}
        siteName={activeSite.name}
        onClose={() => setIsAiSectionModalOpen(false)}
        onSectionGenerated={(generatedSection) => {
          addCustomSectionToActivePage(generatedSection, insertIndex);
          onClose();
        }}
      />
    )}
  </>
  );
};
